import type { PropertyForestContext } from "@/api/propertyForestContext";
import type { PropertyRiskOverlay } from "@/api/propertyHazardReport";
import type { FmMapLegendLevel } from "@/components/ui/mapLegendPalette";
import type {
  MapWorkspaceSelection,
  MapWorkspaceViewport,
} from "@/api/mapWorkspaceState";

export type FmMapSlotInteractionMode = "static" | "pan" | "select" | "explore";
export type MapViewportChangeOrigin =
  | "user"
  | "semantic"
  | "programmatic"
  // A workspace-chrome reflow re-fit of a retained auto-fit. It relocates the
  // rendered viewport to the same canonical geometry under new occlusion, so it
  // must be persisted (rendered==persisted), but it is not a fresh user command.
  | "reflow";
export type MapViewportPosition = Pick<
  MapWorkspaceViewport,
  "center_lat" | "center_lng" | "zoom"
>;
export type MapViewportChangeHandler = (
  viewport: MapWorkspaceViewport,
  origin: MapViewportChangeOrigin,
) => void;

export interface VisualizationGeoJson extends GeoJSON.FeatureCollection {
  _timestamp?: number;
  _propertyId?: string | null;
  _inventoryRevision?: string;
}

export const FM_MEASUREMENT_PLOT_MAPPING_STATUSES = [
  "auto",
  "manual",
  "auto_review",
  "unmapped",
] as const;
export type FmMeasurementPlotMappingStatus =
  (typeof FM_MEASUREMENT_PLOT_MAPPING_STATUSES)[number];

export const FM_MAP_SELECTED_FEATURE_KINDS = [
  "property",
  "stand",
  "plot",
  "risk",
] as const;
export type FmMapSelectedFeatureKind =
  (typeof FM_MAP_SELECTED_FEATURE_KINDS)[number];

export interface FmMeasurementPlotRef {
  id: string;
  label: string;
  mappingStatus: FmMeasurementPlotMappingStatus;
  standId?: string;
}

/** Only the authoritative geometry and popup facts required by the map renderer. */
export interface FmMeasurementPlotOverlayFeature {
  id: number;
  sample_id_external: string;
  name: string;
  area_m2: number | null;
  plot_shape: string;
  measure_method_type: string;
  measurement_method?: string;
  geometry_is_inferred: boolean;
  tree_count: number;
  radius_m: number | null;
  scan_width_m: number | null;
  scan_length_m: number | null;
  width_m: number | null;
  length_m: number | null;
  center_latitude: number | null;
  center_longitude: number | null;
  bearing_deg: number | null;
  campaignLabel?: string;
  measurementDate?: string | null;
}

/** Resolved measurement evidence rendered by the shared primary-map primitive. */
export interface FmMeasurementPlotOverlayData {
  plots: readonly FmMeasurementPlotOverlayFeature[];
  mappingStatuses: Readonly<Record<string, FmMeasurementPlotMappingStatus>>;
  campaignName?: string;
  measurementDate?: string | null;
}

/** Renderer-neutral, resolved data for the canonical primary Map slot. */
export interface FmPrimaryMapWorkspaceData {
  activePropertyId: string | null;
  propertyForestContext: PropertyForestContext | null;
  propertyForestContextLoading: boolean;
  riskOverlay: PropertyRiskOverlay | null;
  riskInventoryRevision: string | null;
  restoredSelection: MapWorkspaceSelection | null;
  restoredViewport: MapViewportPosition | null;
  visualizationData: VisualizationGeoJson | null;
  measurementPlotOverlay?: FmMeasurementPlotOverlayData | null;
  /**
   * Resolved dated raster overlay for Analysis imagery. Image URLs live here,
   * not on slot props: the renderer consumes published data and never fetches.
   */
  datedRasterOverlay?: FmDatedRasterOverlay | null;
  onViewportChange: MapViewportChangeHandler;
}

/** One dated PNG, or a chronological pair so change is visible on the map. */
export type FmDatedRasterComparedWith = {
  imageUrl: string;
  bounds: readonly [number, number, number, number];
  capturedOn: string;
};

/** Leaflet ImageOverlay payload: WGS84 bounds as [west, south, east, north]. */
export type FmDatedRasterOverlay =
  | {
      status: "ready";
      imageUrl: string;
      bounds: readonly [number, number, number, number];
      capturedOn: string;
      comparedWith?: FmDatedRasterComparedWith;
    }
  | {
      status: "unavailable";
      reason: string;
    };

export type FmRankedSpatialRecord =
  | {
      id: string;
      label: string;
      geometryRef: string | null;
      sourceId: string;
      value: number;
      level: FmMapLegendLevel;
    }
  | {
      id: string;
      label: string;
      geometryRef: string | null;
      sourceId: string;
      value: null;
      level?: never;
    };

/** Resolved, page-neutral evidence for a ranked list with explicitly optional stand geometry. */
export interface FmRankedSpatialMapData {
  propertyId: string;
  records: readonly FmRankedSpatialRecord[];
  revision: string;
}
